import React from 'react'
import { Select as MuiSelect, type SelectProps as MuiSelectProps } from '@mui/material'
import { styled } from '@mui/material/styles'

const StyledSelect = styled(MuiSelect)(({ theme, error, disabled, labelId }) => ({
  backgroundColor: disabled ? theme.palette.action.disabledInput : 'white',
  fontSize: 16,
  fontFamily: theme.typography.body2.fontFamily,

  '& .MuiSelect-select': {
    border: 'none',
    borderRadius: '4px',
    padding: '8.5px 12px',
    transition: theme.transitions.create(['box-shadow']),
  },

  '& .MuiInputBase-root': {
    height: '40px',
  },

  '& .MuiOutlinedInput-notchedOutline': {
    ...(labelId
      ? { borderColor: theme.palette.input.default }
      : {
          border: 'none',
          boxShadow: `inset 0 0 0 ${error ? '2px' : '1px'} ${
            disabled ? theme.palette.input.disabled : theme.palette.input.default
          }`,
          borderRadius: 4,
        }),
  },

  '&:hover .MuiOutlinedInput-notchedOutline': {
    ...(labelId ? { border: `2px solid ${theme.palette.input.hover}` } : {}),
  },

  ...(!labelId && {
    '&:hover .MuiOutlinedInput-notchedOutline': {
      boxShadow: disabled
        ? `inset 0 0 0 1px ${theme.palette.text.disabled}`
        : error
          ? `inset 0 0 0 2px ${theme.palette.error.main}`
          : `inset 0 0 0 2px ${theme.palette.input.hover}`,
    },

    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      boxShadow: `inset 0 0 0 2px ${theme.palette.input.hover}, 0 0 0 2px ${theme.palette.input.focus}`,
    },
  }),

  '& .MuiSelect-icon': {
    color: disabled ? theme.palette.text.disabled : theme.palette.text.secondary,
  },
})) as unknown as typeof MuiSelect

interface BaseSelectProps<Value = unknown> extends Omit<MuiSelectProps<Value>, 'variant'> {
  // Force consistent variant
  variant?: 'outlined'
}

const Select = <Value = unknown,>({
  variant = 'outlined',
  ...props
}: BaseSelectProps<Value>): React.ReactElement => {
  return <StyledSelect variant={variant} {...props} />
}

export default Select
